/**
 * React Query hooks for organization data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createOrganization,
  getMyOrganization,
  updateOrganization,
  createOrganizationInvite,
  getOrganizationInvites,
  cancelOrganizationInvite,
  getOrganizationMembers,
  removeOrganizationMember,
  getPendingInvites,
  respondToInvite,
} from "@/services/organizationService";
import type {
  OrganizationCreateData,
  OrganizationUpdateData,
  OrganizationInviteCreateData,
  OrganizationInviteRespondData,
} from "@/types/organization";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch the current user's organization.
 */
export function useMyOrganization() {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["organization", "me"],
    queryFn: getMyOrganization,
    staleTime: 60000, // 1 minute
    enabled: isApiReady,
    retry: false, // Don't retry on 404 (user has no org)
  });
}

/**
 * Hook to create an organization.
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationCreateData) => createOrganization(data),
    onSuccess: () => {
      // Invalidate organization queries
      queryClient.invalidateQueries({ queryKey: ["organization"] });
      // Invalidate user query since user is now linked to org
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

/**
 * Hook to update an organization.
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: number; data: OrganizationUpdateData }) =>
      updateOrganization(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}

/**
 * Hook to fetch all invites for an organization (owner only).
 */
export function useOrganizationInvites(orgId: number | undefined) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["organization", orgId, "invites"],
    queryFn: () => getOrganizationInvites(orgId!),
    staleTime: 0, // Always fetch fresh
    gcTime: 30000,
    enabled: isApiReady && !!orgId,
  });
}

/**
 * Hook to fetch pending invites for the current user.
 */
export function usePendingInvites() {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["invites", "pending"],
    queryFn: getPendingInvites,
    staleTime: 30000, // 30 seconds
    enabled: isApiReady,
  });
}

/**
 * Hook to create an organization invite.
 */
export function useCreateOrganizationInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: number; data: OrganizationInviteCreateData }) =>
      createOrganizationInvite(orgId, data),
    onSuccess: (_data, variables) => {
      // Invalidate invites queries (both user's pending and org's sent invites)
      queryClient.invalidateQueries({ queryKey: ["invites"] });
      queryClient.invalidateQueries({ queryKey: ["organization", variables.orgId, "invites"] });
    },
  });
}

/**
 * Hook to cancel a pending organization invite.
 */
export function useCancelOrganizationInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, inviteId }: { orgId: number; inviteId: number }) =>
      cancelOrganizationInvite(orgId, inviteId),
    onSuccess: (_data, variables) => {
      // Invalidate invites queries
      queryClient.invalidateQueries({ queryKey: ["invites"] });
      queryClient.invalidateQueries({ queryKey: ["organization", variables.orgId, "invites"] });
    },
  });
}

/**
 * Hook to respond to an invite (accept or decline).
 */
export function useRespondToInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inviteId, data }: { inviteId: number; data: OrganizationInviteRespondData }) =>
      respondToInvite(inviteId, data),
    onSuccess: async () => {
      // Refetch queries to ensure UI updates immediately
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["invites", "pending"] }),
        queryClient.refetchQueries({ queryKey: ["organization", "me"] }),
        queryClient.refetchQueries({ queryKey: ["user", "me"] }),
      ]);
    },
  });
}

/**
 * Hook to fetch organization members.
 */
export function useOrganizationMembers(orgId: number | undefined) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["organization", orgId, "members"],
    queryFn: () => getOrganizationMembers(orgId!),
    staleTime: 0, // Always fetch fresh
    gcTime: 30000,
    enabled: isApiReady && !!orgId,
  });
}

/**
 * Hook to remove a member from the organization.
 */
export function useRemoveOrganizationMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: number; userId: number }) =>
      removeOrganizationMember(orgId, userId),
    onSuccess: async (_data, variables) => {
      // Refetch members query immediately
      await queryClient.refetchQueries({ queryKey: ["organization", variables.orgId, "members"] });
    },
  });
}
